import { useEffect } from 'react'
import type { Quapp } from '../types/quapp'

const DEFAULT_TITLE = 'Quapp Store'
const DEFAULT_THEME_COLOR = '#FF9500'
// Toolbar/status-bar color while a Quapp is open — light, to match app UIs and
// the white PWA splash. (Per-Quapp brand colors could come from the DB later.)
const QUAPP_THEME_COLOR = '#ffffff'

/**
 * Makes the browser tab, the installed-PWA identity, and iOS "Add to Home
 * Screen" all reflect the currently-open Quapp instead of the generic
 * "Quapp Store" branding from index.html:
 *
 *  - document.title + apple-mobile-web-app-title  -> the Quapp's name
 *  - apple-touch-icon + favicon                   -> the Quapp's icon
 *  - <link rel="manifest">                        -> a per-Quapp manifest
 *    (name, icons, start_url/scope) so Android installs it with its own identity
 *
 * Everything is restored to the store defaults on unmount / when the Quapp
 * changes, and the generated manifest object-URL is revoked.
 */
export function useQuappHomeScreenMeta(quapp: Quapp | undefined) {
  useEffect(() => {
    if (!quapp) return

    const prevTitle = document.title
    document.title = quapp.display_name
    const appTitle = upsertMeta('apple-mobile-web-app-title', quapp.display_name)

    // Match the browser/standalone toolbar to the app UI (light), restored on exit.
    const themeColor = upsertMeta('theme-color', QUAPP_THEME_COLOR)

    let touchIcon: HTMLLinkElement | null = null
    let favicon: HTMLLinkElement | null = null
    let prevHref: string | null = null
    let prevType: string | null = null
    let manifestLink: HTMLLinkElement | null = null

    if (quapp.icon_url) {
      // iOS home-screen icon (no static apple-touch-icon exists in index.html)
      touchIcon = document.createElement('link')
      touchIcon.setAttribute('rel', 'apple-touch-icon')
      touchIcon.setAttribute('href', quapp.icon_url)
      document.head.appendChild(touchIcon)

      // Browser tab / Android favicon: override the static one, restore on cleanup
      favicon = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]')
      if (favicon) {
        prevHref = favicon.getAttribute('href')
        prevType = favicon.getAttribute('type')
        favicon.setAttribute('href', quapp.icon_url)
        favicon.removeAttribute('type')
      }

      // Per-Quapp web app manifest. MUST be a real, same-origin URL — Chrome
      // rejects blob:/data: manifests for installability. Served as a static
      // file from S3 at /m/<slug>.webmanifest (see scripts/generate-manifest).
      manifestLink = document.createElement('link')
      manifestLink.setAttribute('rel', 'manifest')
      manifestLink.setAttribute('href', `/m/${quapp.slug}.webmanifest`)
      document.head.appendChild(manifestLink)
    }

    return () => {
      document.title = prevTitle
      appTitle.setAttribute('content', DEFAULT_TITLE)
      themeColor.setAttribute('content', DEFAULT_THEME_COLOR)
      if (touchIcon) touchIcon.remove()
      if (favicon) {
        if (prevHref !== null) favicon.setAttribute('href', prevHref)
        if (prevType !== null) favicon.setAttribute('type', prevType)
        else favicon.removeAttribute('type')
      }
      if (manifestLink) manifestLink.remove()
    }
  }, [quapp])
}

function upsertMeta(name: string, content: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
  return el
}
